import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import Bowser from 'bowser';
import { catchError, filter, map, mergeMap, of, switchMap, take, tap } from 'rxjs';
import { TourService } from '../../../tours/services/tour/tour.service';
import { selectIsTourProgressLoaded, selectTourProgress } from '../../../tours/store/selectors/tour-progress.selector';
import { getUserSuccess } from '../../../user/store/actions/user.actions';
import { AuthService } from '../../services/auth/auth.service';
import { ExternalUrlsService } from '../../services/external-urls/external-urls.service';
import { LocalStorageService } from '../../services/local-storage/local-storage.service';
import { UsStatesService } from '../../services/us-states/us-states.service';
import {
  getExternalUrlsFailure,
  getExternalUrlsSuccess,
  getUsStatesFailure,
  getUsStatesSuccess,
  setBrowserName,
  setBrowserNameSuccess,
  setLocalStorage,
  setTheme,
  startTourIfUnseen,
} from '../actions/app.actions';

// App-wide data loading + preference effects. Navigation effects live in
// AppNavigationEffects and dialog effects in AppDialogEffects; all three are
// registered together in app.config.ts.
@Injectable()
export class AppEffects {
  private actions$ = inject(Actions);
  private externalUrlsService = inject(ExternalUrlsService);
  private usStatesService = inject(UsStatesService);
  private store = inject(Store);
  private authService = inject(AuthService);
  private localStorageService = inject(LocalStorageService);
  private tourService = inject(TourService);
  private router = inject(Router);

  setTheme$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(setTheme),
        tap(action => {
          document.body.style.setProperty('color-scheme', action.theme);
          document.body.classList.toggle('theme-dark', action.theme === 'dark');
          localStorage.setItem('theme', action.theme);
          this.authService.theme = action.theme;
        })
      );
    },
    { dispatch: false }
  );

  setBrowserName$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(setBrowserName),
      map(() => {
        const parser = Bowser.getParser(window.navigator.userAgent);
        const browserName = parser.getBrowserName();
        return setBrowserNameSuccess({ browserName });
      })
    );
  });

  getExternalUrls$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(getUserSuccess),
      mergeMap(() => {
        return this.externalUrlsService.getExternalUrls().pipe(
          map(externalUrls => {
            return getExternalUrlsSuccess({ externalUrls });
          }),
          catchError(err => {
            return of(getExternalUrlsFailure({ error: err.error?.errors }));
          })
        );
      })
    );
  });

  getUsStates$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(getExternalUrlsSuccess),
      mergeMap(() => {
        return this.usStatesService.getUsStates().pipe(
          map(usStates => {
            return getUsStatesSuccess({ usStates });
          }),
          catchError(err => {
            return of(getUsStatesFailure({ error: err.error?.errors }));
          })
        );
      })
    );
  });

  setLocalStorage$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(setLocalStorage),
        tap(action => {
          this.localStorageService.set(action.key, action.value);
        })
      );
    },
    { dispatch: false }
  );

  startTourIfUnseen$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(startTourIfUnseen),
        // switchMap, not mergeMap: only the most recently requested tour is still relevant. With
        // mergeMap every queued gate fired in the same tick once isLoaded flipped, so landing on /home
        // and navigating to /medications before the GET returned started the *home* tour over the
        // medications page — against targets that don't exist there — and left the earlier
        // subscriptions alive forever whenever isLoaded never flipped.
        switchMap(action => {
          // The route at dispatch time. A tour is only meaningful on the page that asked for it.
          const requestedFromUrl = this.routePath(this.router.url);

          // Wait until tour progress has loaded from the server before deciding.
          // If it never loads (load failure), the gate stays closed and no tour is shown this session.
          return this.store.select(selectIsTourProgressLoaded).pipe(
            filter(isLoaded => isLoaded),
            take(1),
            concatLatestFrom(() => this.store.select(selectTourProgress)),
            tap(([, progress]) => {
              if (this.routePath(this.router.url) !== requestedFromUrl) {
                return; // navigated away while waiting; the new page starts its own tour
              }

              const seenVersion = progress.find(p => p.tourTypeId === action.tourType)?.seenVersion ?? 0;
              const currentVersion = this.tourService.getCurrentVersion(action.tourType);
              if (currentVersion > seenVersion) {
                this.tourService.start(action.tourType, seenVersion);
              }
            })
          );
        })
      );
    },
    { dispatch: false }
  );

  private routePath(url: string): string {
    return url.split(/[?#]/)[0];
  }
}
